import React, { useMemo } from "react";
import { useTranslation } from "@lib/i18n";
import { string } from "prop-types";

import Typography from "../shared/Typography";
import {
  CTA_SOL_TYPES,
  PROGRESS_BAR_STAGES,
} from "@shared/constants";
import { useDetectDevice } from "@hooks/useDetectDevice";
import { FIXED_ELEMENTS_ATTRIBUTE_NAME } from "@hooks/useScrollToView";
import { useIsIndirectSalesEnabledSelector } from "@redux/reducers/appConfig";
import { twMerge } from "tailwind-merge";

const ProgressBar = ({ activeStage }) => {
  const { t } = useTranslation();
  const { isDesktop } = useDetectDevice();
  const isIndirectSalesEnabled = useIsIndirectSalesEnabledSelector();

  // Filter out Vehicle Registration step when indirect sales is enabled
  const stages = useMemo(() => {
    if (isIndirectSalesEnabled) {
      return PROGRESS_BAR_STAGES.filter(
        stage =>
          !stage.lastCta.includes(CTA_SOL_TYPES.PERSONAL_DETAILS_CONFIRMED)
      );
    }
    return PROGRESS_BAR_STAGES;
  }, [isIndirectSalesEnabled]);

  const wrapperAttributes = useMemo(
    () => ({
      [FIXED_ELEMENTS_ATTRIBUTE_NAME]: isDesktop,
    }),
    [isDesktop]
  );

  return (
    <div
      className="
        hidden w-full h-15 bg-white/90 backdrop-blur-md border-b-4 border-b-greyLight shadow-sm
        sticky top-30 left-0 right-0 z-20 lg:flex
        before:content-[''] before:w-8 before:h-px before:shrink-0 before:block
        after:content-[''] after:w-8 after:h-px after:shrink-0 after:block
      "
      {...wrapperAttributes}
    >
      <div
        className="flex gap-4 w-full max-w-pageContent h-15 m-auto invisible-scrollbars overflow-auto whitespace-nowrap
        snap-x snap-mandatory scroll-px-4
        "
      >
        {stages.map((stage, index) => {
          const isSelected = stage.lastCta.indexOf(activeStage) > -1;

          return (
            <div
              className={twMerge(
                "flex items-center gap-2 relative px-1 snap-start",
                isSelected &&
                  "after:content-[''] after:bg-primaryOrange after:absolute after:inset-0 after:top-auto after:h-2"
              )}
              key={stage.lastCta[0]}
            >
              <Typography
                className={twMerge(
                  isSelected ? "text-primaryOrange" : "text-primaryDark",
                  "text-lg uppercase font-semibold"
                )}
              >
                {index + 1}
              </Typography>
              <Typography
                className={twMerge(
                  isSelected ? "text-primaryOrange" : "text-primaryDark",
                  "text-sm uppercase font-semibold"
                )}
              >
                {t(stage.title)}
              </Typography>
            </div>
          );
        })}
      </div>
    </div>
  );
};

ProgressBar.propTypes = {
  activeStage: string,
};

export default ProgressBar;
